/**
 * VestigeSkillEffect — Conjuration trunk passive: resonance mark on summon death.
 *
 * Always-on once unlocked. When any summon dies (wisp, familiar, shade — any
 * archetype), a resonance mark zone is created at the death position.
 * The mark persists for 8 seconds. Enemies within 1.5 tiles of the mark take
 * 10% more damage from all sources.
 *
 * Mechanic:
 * - `onSummonDied(x, y)` is called by the integration layer when SummonSystem
 *   emits a 'summon-died' event. Vestige creates a 'vestige_mark' zone via
 *   ZoneSystem.create().
 * - The zone's damage bonus is applied via StatusEffectSystem: on zone entry,
 *   enemies receive 'damage_vulnerability' debuff (10%); on exit, it's removed.
 *   This uses the status-effect approach recommended in the spec (vs. hit-time
 *   zone queries), which handles multiple overlapping marks cleanly through
 *   debuff refresh (not double-application).
 *
 * UpgradeEffectResolver keys (scoped to 'vestige' — not tree scope):
 * - markDurationMs: additive, base 8000ms
 * - markRadiusTiles: additive, base 1.5
 * - markDamageBonus: additive, base 0.1
 *
 * Resolver scope isolation: uses 'vestige' skillId to avoid contamination from
 * Blades tree's 'markDurationMs' which is scoped to 'temporal_mark'/'phase_mark'.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Vestige
 * @see compatibility audit Section 1.1 (markDurationMs scope)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface VestigeZoneConfig {
  readonly id: string;
  readonly type: string;
  readonly position: { x: number; y: number };
  readonly durationMs: number;
  readonly sourceSkillId: string;
  readonly shape: 'circle';
  readonly radiusTiles: number;
  readonly damageBonus: number;
}

export interface VestigeDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly zones: {
    create(config: VestigeZoneConfig): string;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface VestigeConfig {
  readonly baseMarkDurationMs: number;
  readonly baseMarkRadiusTiles: number;
  readonly baseMarkDamageBonus: number;
}

const DEFAULT_CONFIG: VestigeConfig = {
  baseMarkDurationMs: 8000,      // spec § Vestige markDurationMs
  baseMarkRadiusTiles: 1.5,      // spec § Vestige markRadiusTiles
  baseMarkDamageBonus: 0.1,      // spec § Vestige markDamageBonus (10%)
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class VestigeSkillEffect implements SkillEffect {
  readonly skillId = 'vestige';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: VestigeDeps;
  private readonly config: VestigeConfig;

  // Observable state for scenarios
  private _marksCreated = 0;
  private _lastZoneId = '';
  private _lastResolvedDurationMs = 0;
  private _lastResolvedRadiusTiles = 0;
  private _lastResolvedDamageBonus = 0;
  private readonly _createCalls: Array<{ config: VestigeZoneConfig }> = [];

  constructor(deps: VestigeDeps, config: Partial<VestigeConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Vestige-specific API (called by integration layer on summon death) ---

  /**
   * Called when any summon dies. Creates a resonance mark zone at the
   * death position. Scoped to 'vestige' skillId for resolver isolation.
   */
  onSummonDied(x: number, y: number): void {
    const durationBonus = this.deps.resolver.getAdditiveEffect('vestige', 'vestigeMarkDurationMs');
    const radiusBonus = this.deps.resolver.getAdditiveEffect('vestige', 'markRadiusTiles');
    const damageBonus = this.deps.resolver.getAdditiveEffect('vestige', 'markDamageBonus');

    const resolvedDurationMs = this.config.baseMarkDurationMs + durationBonus;
    const resolvedRadiusTiles = this.config.baseMarkRadiusTiles + radiusBonus;
    const resolvedDamageBonus = this.config.baseMarkDamageBonus + damageBonus;

    this._lastResolvedDurationMs = resolvedDurationMs;
    this._lastResolvedRadiusTiles = resolvedRadiusTiles;
    this._lastResolvedDamageBonus = resolvedDamageBonus;

    const zoneConfig: VestigeZoneConfig = {
      id: `vestige_mark_${this._marksCreated}`,
      type: 'vestige_mark',
      position: { x, y },
      durationMs: resolvedDurationMs,
      sourceSkillId: 'vestige',
      shape: 'circle',
      radiusTiles: resolvedRadiusTiles,
      damageBonus: resolvedDamageBonus,
    };

    this._createCalls.push({ config: zoneConfig });
    this._lastZoneId = this.deps.zones.create(zoneConfig);
    this._marksCreated++;
  }

  // --- State getters ---

  get marksCreated(): number { return this._marksCreated; }
  get lastZoneId(): string { return this._lastZoneId; }
  get lastResolvedDurationMs(): number { return this._lastResolvedDurationMs; }
  get lastResolvedRadiusTiles(): number { return this._lastResolvedRadiusTiles; }
  get lastResolvedDamageBonus(): number { return this._lastResolvedDamageBonus; }
  get createCalls(): readonly { config: VestigeZoneConfig }[] { return this._createCalls; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return false; // passive — not manually activated
  }

  activate(): boolean {
    return false;
  }

  isActive(): boolean {
    return false;
  }

  get cooldown(): number { return 0; }

  update(_delta: number): void {
    // Passive — no frame logic
  }

  destroy(): void {
    this._createCalls.length = 0;
  }
}
